import { useEffect, useRef } from 'react'
import { useAnimationFrame } from 'hooks'

import { Canvas } from './confettiStyle'

const maxParticleCount = 30
const particleSpeed = 1
const frameInterval = 20
const colors = [
  '#f44336',
  '#e91e63',
  '#9c27b0',
  '#673ab7',
  '#3f51b5',
  '#2196f3',
  '#03a9f4',
  '#00bcd4',
  '#009688',
  '#4caf50',
  '#8bc34a',
  '#cddc39',
  '#ffeb3b',
  '#ffc107',
  '#ff9800',
  '#ff5722',
  '#795548',
  '#607d8b',
]

const makeParticle = (width, height) => ({
  color: colors[(Math.random() * colors.length) | 0],
  x: Math.random() * width,
  y: Math.random() * height - height,
  diameter: Math.random() * 10 + 5,
  tilt: Math.random() * 10 - 10,
  tiltAngleIncrement: Math.random() * 0.07 + 0.05,
  tiltAngle: 0,
})

const Confetti = props => {
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  const canvasRef = useRef()
  const lastFrameTime = useRef(Date.now())
  const particles = useRef([])
  const waveAngle = useRef(0)
  const ctx = useRef()

  const drawParticles = () => particles.current.forEach(particle => {
    ctx.current.beginPath()
    ctx.current.lineWidth = particle.diameter
    ctx.current.strokeStyle = particle.color
    const x = particle.x + particle.tilt
    ctx.current.moveTo(x + particle.diameter / 2, particle.y)
    ctx.current.lineTo(x, particle.y + particle.tilt + particle.diameter / 2)
    ctx.current.stroke()
  })

  const updateParticles = () => {
    waveAngle.current += 0.01

    particles.current = particles.current.map(particle => {
      if (particle.x > canvasRef.current.width + 20 || particle.x < -20 || particle.y > canvasRef.current.height) {
        return makeParticle(canvasRef.current.width, canvasRef.current.height)
      } else {
        return {
          ...particle,
          tiltAngle: particle.tiltAngle + particle.tiltAngleIncrement,
          x: particle.x + Math.sin(waveAngle.current),
          y: particle.y + (Math.cos(waveAngle.current) + particle.diameter + particleSpeed) * .5,
          tilt: Math.sin(particle.tiltAngle) * 15,
        }
      }
    }).filter(Boolean)
  }

  useEffect(() => {
    if (canvasRef.current) {
      ctx.current = canvasRef.current.getContext('2d')
      while (particles.current.length < maxParticleCount) {
        particles.current.push(makeParticle(canvasRef.current.width, canvasRef.current.height))
      }
    }
  }, [canvasRef])

  useAnimationFrame(() => {
    if (!canvasRef.current) return
    const now = Date.now()
    const delta = now - lastFrameTime.current
    if (delta > frameInterval) {
      ctx.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
      updateParticles()
      drawParticles()
      lastFrameTime.current = now - (delta % frameInterval)
    }
  })

  return !motionQuery.matches && (
    <Canvas ref={canvasRef} {...props} />
  )
}

export default Confetti